import type { PDFDocumentProxy, PDFPageProxy, RenderTask } from 'pdfjs-dist';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { workflowsApi } from '@/api/workflows';
import { message } from '@/lib/message';

import { pdfjsLib } from './pdfFieldMapping';

/** One AcroForm widget occurrence with its on-canvas (CSS px) geometry. */
type Widget = {
  key: string;
  name: string;
  fieldType: string;
  /** Export ("on") state of a checkbox/radio widget, when present. */
  exportValue?: string;
  /**
   * The string a mapping stores/compares against. For a checkbox/radio widget
   * whose AcroForm field name is shared by several widgets (BMO Title / Gender /
   * Language), this is `name:exportValue` so the specific widget can be targeted
   * — matching fillPdf.ts's multi-widget handling. Otherwise it's just `name`.
   */
  mappingKey: string;
  pageIndex: number;
  left: number;
  top: number;
  width: number;
  height: number;
};

type PageDim = { width: number; height: number };

type Props = {
  templateId: string;
  /** S3 object key of the PDF to render (EN or FR variant). */
  fileKey: string;
  /** Lower-cased search term; dims non-matching widget names. */
  search: string;
  /** The active form field's AcroForm names in the selected PDF — their widgets
   *  render as "selected". */
  activeNames: Set<string>;
  /**
   * AcroField name → breadcrumb labels of the OTHER form fields already mapped to
   * it, in workflow (waterfall fill) order. Mapping is many-to-many, so a box can
   * legitimately carry several — clicking it adds the active field alongside them
   * rather than taking it over.
   */
  assignedNames: Map<string, string[]>;
  /** No active field selected, or the workflow is read-only → clicks are inert. */
  disabled: boolean;
  onPick: (fieldName: string) => void;
};

const SCALE = 1.3;

/**
 * Renders every page of an uploaded PDF to a canvas via pdf.js and overlays a
 * clickable box on each AcroForm widget. Canvas rasterization is imperative and
 * runs once per document load; the overlay boxes are React-rendered so search /
 * active-selection / assigned-state restyling stays cheap (no re-raster).
 */
export function PdfAcroViewer({
  templateId,
  fileKey,
  search,
  activeNames,
  assignedNames,
  disabled,
  onPick,
}: Props) {
  const { t } = useTranslation();
  const [pages, setPages] = useState<PageDim[]>([]);
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  // Retain the parsed page proxies between the parse pass (which sets `pages`)
  // and the render pass (which draws into the canvases React just mounted).
  const pageProxiesRef = useRef<PDFPageProxy[]>([]);
  const docRef = useRef<PDFDocumentProxy | null>(null);

  // Parse pass: fetch bytes, extract page dims + widget geometry.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPages([]);
    setWidgets([]);
    pageProxiesRef.current = [];

    (async () => {
      try {
        const url = await workflowsApi.createDownloadUrl(templateId, fileKey);
        // Fetch the bytes ourselves (XHR) rather than handing pdf.js the URL, so
        // one presigned GET drives the whole document. Requires the templates
        // bucket to allow GET from this origin (upload PUT already does).
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.arrayBuffer();
        if (cancelled) return;

        const doc = await pdfjsLib.getDocument({ data }).promise;
        if (cancelled) {
          void doc.destroy();
          return;
        }
        docRef.current = doc;

        const dims: PageDim[] = [];
        const found: Widget[] = [];
        const proxies: PDFPageProxy[] = [];
        for (let p = 1; p <= doc.numPages; p++) {
          const page = await doc.getPage(p);
          if (cancelled) return;
          proxies.push(page);
          const viewport = page.getViewport({ scale: SCALE });
          dims.push({ width: viewport.width, height: viewport.height });

          const annots = await page.getAnnotations();
          for (const a of annots) {
            // Widget annotations that carry a field name are the AcroForm fields.
            // Push/radio/checkbox groups produce multiple widgets sharing one
            // name — we draw every box but assign by name.
            if (a.subtype !== 'Widget' || !a.fieldName) continue;
            const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(a.rect);
            // pdf.js exposes the checkbox/radio "on" state as `exportValue`; the
            // TS annotation type is loose, so read it defensively.
            const exportValue = (a as { exportValue?: string }).exportValue || undefined;
            found.push({
              key: `${p}:${a.id}`,
              name: a.fieldName,
              fieldType: a.fieldType ?? '',
              exportValue,
              mappingKey: a.fieldName, // finalized in the post-pass below
              pageIndex: p - 1,
              left: Math.min(x1, x2),
              top: Math.min(y1, y2),
              width: Math.abs(x2 - x1),
              height: Math.abs(y2 - y1),
            });
          }
        }
        if (cancelled) return;
        // Finalize each widget's mapping key: a checkbox/radio widget whose field
        // name is shared by more than one widget (multi-widget group) carries its
        // export value as `name:exportValue`; every other widget keys by name.
        const nameCounts = new Map<string, number>();
        for (const w of found) nameCounts.set(w.name, (nameCounts.get(w.name) ?? 0) + 1);
        for (const w of found) {
          const shared = (nameCounts.get(w.name) ?? 0) > 1;
          w.mappingKey = w.exportValue && shared ? `${w.name}:${w.exportValue}` : w.name;
        }
        pageProxiesRef.current = proxies;
        setPages(dims);
        setWidgets(found);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        message.error(t('pdfMapping.loadPdfFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (docRef.current) {
        void docRef.current.destroy();
        docRef.current = null;
      }
    };
  }, [templateId, fileKey, t]);

  // Render pass: draw each parsed page into the canvas React mounted for it.
  useEffect(() => {
    if (pages.length === 0) return;
    const tasks: RenderTask[] = [];
    const dpr = window.devicePixelRatio || 1;
    pages.forEach((dim, i) => {
      const canvas = canvasRefs.current[i];
      const page = pageProxiesRef.current[i];
      if (!canvas || !page) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const viewport = page.getViewport({ scale: SCALE });
      // Backing store scaled by DPR for crisp text; CSS size stays in layout px.
      canvas.width = Math.floor(dim.width * dpr);
      canvas.height = Math.floor(dim.height * dpr);
      canvas.style.width = `${dim.width}px`;
      canvas.style.height = `${dim.height}px`;
      const task = page.render({
        canvasContext: ctx,
        viewport,
        transform: dpr === 1 ? undefined : [dpr, 0, 0, dpr, 0, 0],
      });
      tasks.push(task);
      task.promise.catch(() => {
        /* cancelled on cleanup / re-render — ignore */
      });
    });
    return () => {
      for (const task of tasks) task.cancel();
    };
  }, [pages]);

  const term = search.trim().toLowerCase();
  const widgetsByPage = useMemo(() => {
    const map = new Map<number, Widget[]>();
    for (const w of widgets) {
      const list = map.get(w.pageIndex) ?? [];
      list.push(w);
      map.set(w.pageIndex, list);
    }
    return map;
  }, [widgets]);

  if (error) {
    return (
      <div style={{ padding: 24, color: '#b91c1c', fontSize: 13 }}>
        {t('pdfMapping.loadPdfFailed')}
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{ padding: 24, color: '#6b7280', fontSize: 13 }}>
        {t('pdfMapping.loadingPdf')}
      </div>
    );
  }
  if (widgets.length === 0) {
    return (
      <div style={{ padding: 24, color: '#6b7280', fontSize: 13 }}>
        {t('pdfMapping.noAcroFields')}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
        height: '80vh',
      }}
    >
      {pages.map((dim, i) => (
        <div
          key={i}
          style={{
            position: 'relative',
            width: dim.width,
            height: dim.height,
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
          }}
        >
          <canvas
            ref={(el) => {
              canvasRefs.current[i] = el;
            }}
            style={{ display: 'block' }}
          />
          {(widgetsByPage.get(i) ?? []).map((w) => {
            const isActive = activeNames.has(w.mappingKey);
            const assignedTo = assignedNames.get(w.mappingKey) ?? [];
            const isAssigned = !isActive && assignedTo.length > 0;
            // How many form fields fill this box in total — the active one counts
            // too, since its mapping is excluded from `assignedNames`. Anything
            // above 1 is a shared box that falls back in workflow order.
            const claimants = assignedTo.length + (isActive ? 1 : 0);
            const dim2 = term !== '' && !w.mappingKey.toLowerCase().includes(term);
            const { border, background } = boxStyle(isActive, isAssigned);
            return (
              <button
                type="button"
                key={w.key}
                title={
                  assignedTo.length > 0
                    ? t('pdfMapping.acroAssignedTo', {
                        name: w.mappingKey,
                        fields: assignedTo.join(' · '),
                      })
                    : w.mappingKey
                }
                disabled={disabled}
                onClick={() => onPick(w.mappingKey)}
                style={{
                  position: 'absolute',
                  left: w.left,
                  top: w.top,
                  width: w.width,
                  height: w.height,
                  padding: 0,
                  margin: 0,
                  border,
                  background,
                  borderRadius: 2,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: dim2 ? 0.25 : 1,
                  transition: 'background 0.1s, border-color 0.1s',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: -16,
                    left: 0,
                    fontSize: 9,
                    lineHeight: '14px',
                    padding: '0 3px',
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                    color: '#fff',
                    background: isActive ? '#0179C1' : isAssigned ? '#059669' : '#6b7280',
                    pointerEvents: 'none',
                    maxWidth: 220,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {claimants > 1 ? `${w.mappingKey} ×${claimants}` : w.mappingKey}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function boxStyle(isActive: boolean, isAssigned: boolean): { border: string; background: string } {
  if (isActive) return { border: '2px solid #0179C1', background: 'rgba(1,121,193,0.22)' };
  if (isAssigned) return { border: '1.5px solid #059669', background: 'rgba(5,150,105,0.14)' };
  return { border: '1.5px solid #6b7280', background: 'rgba(156,163,175,0.14)' };
}
